import React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './ActionNotification.module.scss';

export type ActionNotificationType = 'success' | 'error' | 'info';
export type ActionNotificationPosition = 'top-center' | 'bottom-center';

interface ActionNotificationProps {
  open: boolean;
  message: string;
  durationMs?: number;
  type?: ActionNotificationType;
  position?: ActionNotificationPosition;
  className?: string;
  onClose?: () => void;
  disablePortal?: boolean;
}

const DEFAULT_DURATION_MS = 4000;

const ActionNotification: React.FC<ActionNotificationProps> = ({
  open,
  message,
  durationMs = DEFAULT_DURATION_MS,
  type = 'info',
  position = 'top-center',
  className,
  onClose,
  disablePortal = false,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(
      () => {
        onClose?.();
      },
      Math.max(0, durationMs)
    );
    return () => window.clearTimeout(timer);
  }, [open, durationMs, onClose]);

  if (!open) return null;

  const node = (
    <div
      className={clsx(
        styles.container,
        position === 'top-center' ? styles.topCenter : styles.bottomCenter
      )}
      role='status'
      aria-live='polite'
    >
      <div
        className={clsx(
          styles.pill,
          type === 'success' && styles.success,
          type === 'error' && styles.error,
          type === 'info' && styles.info,
          className
        )}
      >
        {message}
      </div>
    </div>
  );

  if (disablePortal) {
    return node;
  }

  if (typeof document === 'undefined') {
    return node;
  }

  return createPortal(node, document.body);
};

export default ActionNotification;
